import { Button as PrimeButton } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Amenity } from "../../../models/amenity.model";
import { amenityService } from "../../../service/amenity.service";
import PageHeader from "../../PageHeader";

const AmenitiesComponent: React.FC = () => {
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [amenity, setAmenity] = useState<Amenity>({
    id: 0,
    name: "",
    description: "",
    parentAmenityId: null,
    iconName: "",
  });
  const [globalFilter, setGlobalFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("amenitiesTablePage");
    return savedPage ? parseInt(savedPage) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRows = localStorage.getItem("amenitiesTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadAmenities();
  }, []);

  const loadAmenities = async () => {
    setLoading(true);
    try {
      const result = await amenityService.getParentAmenities();
      setAmenities(result ?? []);
    } catch (err) {
      console.error("Error fetching amenities", err);
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to fetch amenities.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (amenity.id) {
        await amenityService.update(amenity.id, amenity);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Amenity updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        await amenityService.create(amenity);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Amenity created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      await loadAmenities();
      setShowForm(false);
    } catch (err: any) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save amenity details.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
    setLoading(false);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setAmenity({ ...amenity, [name]: value });
  };

  const handleDelete = async (rowData: Amenity) => {
    if (!rowData.id) return;
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete the amenity "${rowData.name}". This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });
    if (confirm.isConfirmed) {
      setLoading(true);
      try {
        await amenityService.delete(rowData.id);
        setAmenities((prev) => prev.filter((a) => a.id !== rowData.id));
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "Amenity deleted successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } catch {
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "error",
          title: "Failed to delete amenity.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setLoading(false);
    }
  };

  // DataTable pagination handler
  const handlePageChange = (event: any) => {
    const newPage = event.first;
    const newRows = event.rows;
    setCurrentPage(newPage);
    setRowsPerPage(newRows);
    localStorage.setItem("amenitiesTablePage", newPage.toString());
    localStorage.setItem("amenitiesTableRows", newRows.toString());
  };

  const filteredAmenities = amenities.filter((c) =>
    c.name.toLowerCase().includes(globalFilter.toLowerCase())
  );

  const actionBodyTemplate = (rowData: Amenity) => (
    <div className="d-flex gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => {
          setAmenity(rowData);
          setShowForm(true);
        }}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleDelete(rowData)}
        title="Delete"
        style={{ color: "#dc3545" }}
      >
        <Icon.Trash size={20} />
      </Button>
    </div>
  );

  const childAmenityButtonTemplate = (rowData: Amenity) => (
    <Button
      variant="outline-info"
      onClick={() =>
        navigate("/child-amenities", {
          state: {
            amenityIdFromState: rowData.id,
            amenityName: rowData.name,
          },
        })
      }
      style={{ padding: "0.35rem 0.75rem" }}
    >
      <Icon.ArrowRight />
    </Button>
  );
  const handleRefresh = () => loadAmenities();

  return (
    <div className="content">
      <PageHeader
        title="Amenities List"
        beforeTitle={
          <PrimeButton
            icon="pi pi-refresh"
            rounded
            text
            severity="secondary"
            onClick={handleRefresh}
            tooltip="Refresh"
            tooltipOptions={{ position: "bottom" }}
            aria-label="Refresh Amenities"
          />
        }
      />
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center gap-2 my-3">
                <IconField iconPosition="left" className="flex-grow-1">
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setCurrentPage(0);
                      localStorage.setItem("amenitiesTablePage", "0");
                    }}
                    placeholder="Search"
                    style={{ minWidth: 250 }}
                  />
                </IconField>
                <Button
                  className="btn btn-primary ms-2"
                  style={{ minWidth: 160 }}
                  onClick={() => {
                    setAmenity({
                      id: 0,
                      name: "",
                      description: "",
                      parentAmenityId: null,
                      iconName: "",
                    });
                    setShowForm(true);
                  }}
                >
                  <Icon.PlusCircle className="me-1" /> Create Amenity
                </Button>
              </div>
              <DataTable
                value={filteredAmenities}
                dataKey="id"
                paginator
                first={currentPage}
                rows={rowsPerPage}
                totalRecords={filteredAmenities.length}
                onPage={handlePageChange}
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage="No amenities found."
                className="grid-table"
                globalFilter={globalFilter}
              >
                <Column field="name" header="Name" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Child Amenities"
                  body={childAmenityButtonTemplate}
                  style={{ width: "120px", textAlign: "center" }}
                />
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {amenity.id ? "Update Amenity" : "Create Amenity"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name:</label>
                            <input
                              className="form-control"
                              name="name"
                              value={amenity.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="mb-3">
                            <label className="form-label">Description:</label>
                            <input
                              className="form-control"
                              name="description"
                              value={amenity.description}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? amenity.id
                                ? "Updating..."
                                : "Creating..."
                              : amenity.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmenitiesComponent;
